import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { ROUTES } from '@/routes';

import { useMembershipPreviewMode } from './MembershipContentLock';

export type MembershipMonitoringPreviewTutorialAction =
  | 'registerProduct'
  | 'editKeywords'
  | 'addKeyword'
  | 'removeKeyword'
  | 'toggleAlarm'
  | 'downloadExcel'
  | 'deleteProduct';

type TutorialCopy = {
  title: string;
  description: string;
  result: string;
};

type MembershipMonitoringPreviewTutorialContextValue = {
  openTutorial: (action: MembershipMonitoringPreviewTutorialAction) => boolean;
};

const tutorialCopyByAction = {
  registerProduct: {
    title: '모니터링 상품 등록 체험',
    description: '스마트스토어 상품 URL을 입력해 순위 추적 대상 상품으로 등록하는 흐름이에요.',
    result: '구독 후에는 상품을 등록하고 원하는 키워드 기준으로 매일 순위 변화를 수집할 수 있어요.',
  },
  editKeywords: {
    title: '키워드 관리 체험',
    description: '등록된 상품의 추적 키워드를 추가하거나 변경하는 흐름이에요.',
    result: '구독 후에는 상품별로 최대 10개 키워드를 관리하고 순위 추이를 비교할 수 있어요.',
  },
  addKeyword: {
    title: '키워드 추가 체험',
    description: '상세 화면에서 새 추적 키워드를 추가해 모니터링 범위를 넓히는 흐름이에요.',
    result: '구독 후에는 추가한 키워드가 다음 수집 주기부터 순위 데이터에 반영돼요.',
  },
  removeKeyword: {
    title: '키워드 삭제 체험',
    description: '더 이상 추적하지 않을 키워드를 모니터링 대상에서 제외하는 흐름이에요.',
    result: '구독 후에는 불필요한 키워드를 정리하고 핵심 키워드 중심으로 데이터를 볼 수 있어요.',
  },
  toggleAlarm: {
    title: '순위 알림 체험',
    description: '상품 순위 변화를 카카오 알림으로 받아보도록 설정하는 흐름이에요.',
    result: '구독 후에는 매일 오전 수집된 순위 변화를 알림으로 확인할 수 있어요.',
  },
  downloadExcel: {
    title: '순위 데이터 다운로드 체험',
    description: '상품의 키워드별 순위 히스토리를 엑셀 파일로 내려받는 흐름이에요.',
    result: '구독 후에는 수집된 순위 데이터를 파일로 저장해 외부 분석에 활용할 수 있어요.',
  },
  deleteProduct: {
    title: '모니터링 상품 삭제 체험',
    description: '더 이상 추적하지 않을 상품을 모니터링 목록에서 제거하는 흐름이에요.',
    result: '구독 후에는 실제 등록 상품을 삭제하고 모니터링 슬롯을 정리할 수 있어요.',
  },
} satisfies Record<MembershipMonitoringPreviewTutorialAction, TutorialCopy>;

const MembershipMonitoringPreviewTutorialContext =
  createContext<MembershipMonitoringPreviewTutorialContextValue | null>(null);

export const MembershipMonitoringPreviewTutorialProvider = ({ children }: PropsWithChildren) => {
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [activeAction, setActiveAction] =
    useState<MembershipMonitoringPreviewTutorialAction | null>(null);

  const closeTutorial = () => setIsTutorialOpen(false);

  const openTutorial = useCallback(
    (action: MembershipMonitoringPreviewTutorialAction) => {
      if (!isPreviewMode) return false;

      setActiveAction(action);
      setIsTutorialOpen(true);
      return true;
    },
    [isPreviewMode],
  );

  const contextValue = useMemo(() => ({ openTutorial }), [openTutorial]);
  const activeCopy = activeAction ? tutorialCopyByAction[activeAction] : null;

  return (
    <MembershipMonitoringPreviewTutorialContext.Provider value={contextValue}>
      {children}
      <Dialog open={isTutorialOpen} onOpenChange={(open) => !open && closeTutorial()}>
        <DialogContent
          noCloseBtn
          className="w-[500px] max-w-[calc(100vw-32px)] p-[30px]"
          data-membership-preview-interactive="true"
        >
          {activeCopy && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-point-500 Caption1M12">상품 순위 모니터링 체험</span>
                <div className="text-gr-900 Title3S18">{activeCopy.title}</div>
                <div className="text-gr-700 Body5M14">{activeCopy.description}</div>
              </div>
              <div className="rounded-[8px] bg-gr-50 p-4 text-gr-800 Body5M14">
                {activeCopy.result}
              </div>
              <div className="flex gap-3">
                <Button className="h-[44px] flex-1" variant="secondary" onClick={closeTutorial}>
                  계속 둘러보기
                </Button>
                <Button
                  className="h-[44px] flex-1"
                  variant="solid"
                  onClick={() => navigate(ROUTES.moreMembership)}
                >
                  구독하고 사용하기
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MembershipMonitoringPreviewTutorialContext.Provider>
  );
};

export const useMembershipMonitoringPreviewTutorial = () => {
  const context = useContext(MembershipMonitoringPreviewTutorialContext);
  if (!context) {
    throw new Error(
      'useMembershipMonitoringPreviewTutorial must be used within MembershipMonitoringPreviewTutorialProvider',
    );
  }

  return context;
};
